
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash, Save, ArrowUp, ArrowDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogTrigger 
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Updated type to match the database schema
interface FAQItem {
  id: string;
  question: string;
  answer: string;
  order: number;
  created_at?: string;
  updated_at?: string;
}

export function FAQManager() {
  const { toast } = useToast();
  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<FAQItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('faq_items')
        .select('*')
        .order('order', { ascending: true });
        
      if (error) throw error;
      
      setFaqItems(data || []);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      toast({
        title: "Error loading FAQs",
        description: "Could not load FAQ items. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddItem = () => {
    setSelectedItem({
      id: '',
      question: '',
      answer: '',
      order: faqItems.length + 1
    });
    setDialogOpen(true);
  };

  const handleEditItem = (item: FAQItem) => {
    setSelectedItem({ ...item });
    setDialogOpen(true);
  };

  const handleSaveItem = async () => {
    if (!selectedItem || !selectedItem.question.trim() || !selectedItem.answer.trim()) {
      toast({
        title: "Validation Error",
        description: "Both question and answer are required.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSaving(true);
      
      if (selectedItem.id) {
        // Update existing item
        const { error } = await supabase
          .from('faq_items')
          .update({
            question: selectedItem.question,
            answer: selectedItem.answer,
            order: selectedItem.order
          })
          .eq('id', selectedItem.id);
          
        if (error) throw error;
        
        toast({
          title: "FAQ Updated",
          description: "The FAQ item has been updated successfully."
        });
      } else {
        // Insert new item
        const { error } = await supabase
          .from('faq_items')
          .insert({
            question: selectedItem.question,
            answer: selectedItem.answer,
            order: selectedItem.order
          });
          
        if (error) throw error;
        
        toast({
          title: "FAQ Added",
          description: "A new FAQ item has been added successfully."
        });
      }
      
      setDialogOpen(false);
      fetchFAQs();
    } catch (err) {
      console.error('Error saving FAQ:', err);
      toast({
        title: "Error",
        description: "Failed to save FAQ item. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleMoveItem = async (id: string, direction: 'up' | 'down') => {
    const currentIndex = faqItems.findIndex(item => item.id === id);
    if (
      (direction === 'up' && currentIndex === 0) || 
      (direction === 'down' && currentIndex === faqItems.length - 1)
    ) {
      return;
    }
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const swapItem = faqItems[newIndex];
    
    try {
      // Start a transaction for swapping orders
      const updates = [
        supabase
          .from('faq_items')
          .update({ order: swapItem.order })
          .eq('id', id),
        supabase
          .from('faq_items')
          .update({ order: faqItems[currentIndex].order })
          .eq('id', swapItem.id)
      ];
      
      await Promise.all(updates);
      
      toast({
        title: "Order Updated",
        description: "The FAQ order has been updated successfully."
      });
      
      fetchFAQs();
    } catch (err) {
      console.error('Error updating order:', err);
      toast({
        title: "Error",
        description: "Failed to update FAQ order. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;
    
    try {
      const { error } = await supabase
        .from('faq_items')
        .delete()
        .eq('id', selectedItem.id);
        
      if (error) throw error;
      
      toast({
        title: "FAQ Deleted",
        description: "The FAQ item has been deleted successfully."
      });
      
      setDeleteDialogOpen(false);
      fetchFAQs();
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      toast({
        title: "Error",
        description: "Failed to delete FAQ item. Please try again.",
        variant: "destructive"
      });
    }
  };

  const confirmDelete = (item: FAQItem) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 bg-gray-200 rounded-md w-full"></div>
        ))}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Frequently Asked Questions</CardTitle>
        <Button onClick={handleAddItem}>
          <Plus className="mr-2 h-4 w-4" /> Add FAQ
        </Button>
      </CardHeader>
      <CardContent>
        {faqItems.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No FAQ items found. Click the button above to add your first FAQ.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={item.id} className="flex items-start gap-2 p-4 border rounded-md">
                <div className="flex flex-col gap-1 mr-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    disabled={index === 0}
                    onClick={() => handleMoveItem(item.id, 'up')}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    disabled={index === faqItems.length - 1}
                    onClick={() => handleMoveItem(item.id, 'down')}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="flex-1">
                  <h3 className="font-medium">{item.question}</h3>
                  <p className="text-muted-foreground line-clamp-2">{item.answer}</p>
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={() => handleEditItem(item)}>
                    <Save className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => confirmDelete(item)}>
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>{selectedItem?.id ? 'Edit FAQ' : 'Add New FAQ'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="question" className="font-medium">Question</label>
                <Input
                  id="question"
                  value={selectedItem?.question || ''}
                  onChange={(e) => setSelectedItem(prev => prev ? {...prev, question: e.target.value} : null)}
                  placeholder="Enter the question"
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="answer" className="font-medium">Answer</label>
                <Textarea
                  id="answer"
                  value={selectedItem?.answer || ''}
                  onChange={(e) => setSelectedItem(prev => prev ? {...prev, answer: e.target.value} : null)}
                  placeholder="Enter the answer"
                  rows={5}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveItem} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the FAQ item "{selectedItem?.question}".
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteItem}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
